// Spec §69 — full report list with filter chips.
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { reportsApi } from '@/api/reportsApi';
import { ApiException } from '@/api/errors';
import { REPORT_STATUS_LABEL, REPORT_STATUS_TONE, formatRelative } from '@/utils/format';
import type { ReportJson, ReportStatus } from '@/api/types';

type StatusFilter = 'all' | ReportStatus;
const FILTERS: StatusFilter[] = ['all', 'draft', 'submitted', 'archived'];

export function ReportListScreen() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [rows, setRows] = useState<ReportJson[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setBusy(true);
      setError(null);
      try {
        const { rows: data } = await reportsApi.list(getToken, {
          status: filter === 'all' ? undefined : filter,
        });
        if (!cancelled) setRows(data);
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not load reports.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [filter, getToken]);

  return (
    <div className="stack">
      <div className="card">
        <div className="row" style={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                className={`btn ${filter === f ? 'btn--primary' : 'btn--ghost'}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : REPORT_STATUS_LABEL[f]}
              </button>
            ))}
          </div>
          <Link to="/reports/new" className="btn btn--primary">
            New report
          </Link>
        </div>
      </div>

      <div className="card" style={{ padding: 12 }}>
        {error ? <div className="error-banner">{error}</div> : null}
        {busy ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            {filter === 'all'
              ? 'No reports yet — your saved reports will appear here.'
              : `No ${REPORT_STATUS_LABEL[filter].toLowerCase()} reports yet.`}
          </div>
        ) : (
          <div className="list">
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                className="list__item"
                onClick={() => navigate(`/reports/${row.id}`)}
              >
                <div style={{ flex: 1 }}>
                  <div className="list__item-title">
                    {row.title.trim() === '' ? '(Untitled)' : row.title}
                  </div>
                  <div className="list__item-subtitle">
                    <span className={REPORT_STATUS_TONE[row.status]}>
                      {REPORT_STATUS_LABEL[row.status]}
                    </span>
                    <span>· {formatRelative(row.updated_at)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
